import type { FaceKey, FacePhotoSize, ProductColorOption } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useStore } from 'zustand';
import {
  type CompositeSizeMismatch,
  compositeSizeMismatchesOf,
  useEditAreaDraftStore,
} from '@/entities/edit-area';
import { FACE_LABELS } from '../model/face-labels';
import { CompositeAssetSlot } from './composite-asset-slot';

interface CompositeAssetCardProps {
  readonly face: FaceKey;
  readonly colors: readonly Pick<ProductColorOption, 'id' | 'name'>[];
}

const formatPx = ({ widthPx, heightPx }: FacePhotoSize): string => `${widthPx}×${heightPx}`;

/**
 * 면 하나의 합성 에셋 카드 — 마스크(필수)·음영·안내선 칸과 저장되는 multiply 토글(Q10). 마스크와 px 크기가 다른
 * 음영·안내선·색상 사진은 바로 경고하고(Q9), 저장은 계약 검사가 막는다.
 */
export const CompositeAssetCard = ({ face, colors }: CompositeAssetCardProps): ReactElement => {
  const store = useEditAreaDraftStore();
  const state = useStore(store);
  const composite = state.faces[face]?.composite;
  const mismatches = compositeSizeMismatchesOf(state, face);
  const label = FACE_LABELS[face];
  const nameOf = (mismatch: CompositeSizeMismatch): string => {
    if (mismatch.asset !== 'photo') return mismatch.asset === 'shading' ? '음영' : '안내선';
    return `${colors.find((color) => color.id === mismatch.colorOptionId)?.name ?? '색상'} 사진`;
  };
  return (
    <article className="min-w-0 space-y-3 rounded-lg border p-3" aria-label={`${label} 합성 에셋`}>
      <h4 className="font-medium">{label}</h4>
      <div className="grid gap-3 sm:grid-cols-3">
        <CompositeAssetSlot face={face} kind="mask" />
        <CompositeAssetSlot face={face} kind="shading" disabled={composite === undefined} />
        <CompositeAssetSlot face={face} kind="safeArea" disabled={composite === undefined} />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={composite?.multiply ?? false}
          disabled={composite === undefined}
          onChange={(event) => state.setCompositeMultiply(face, event.target.checked)}
        />
        사진에 곱하기(multiply)
        {composite === undefined ? (
          <span className="text-xs text-muted-foreground">마스크를 먼저 올려 주세요.</span>
        ) : null}
      </label>
      {mismatches.length > 0 ? (
        <div role="alert" className="space-y-1 text-sm text-destructive">
          <p>마스크와 px 크기가 다른 에셋이 있습니다. 저장하려면 같은 크기로 맞춰 주세요.</p>
          <ul className="list-inside list-disc">
            {mismatches.map((mismatch) => (
              <li key={mismatch.asset === 'photo' ? mismatch.colorOptionId : mismatch.asset}>
                마스크 {formatPx(mismatch.maskSize)} / {nameOf(mismatch)} {formatPx(mismatch.size)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </article>
  );
};
